import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCredits } from "@/context/CreditsContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Coins, Loader2, ArrowRight, Eye, ArrowLeft, Clock } from "lucide-react";
import { CORE_PLANS as PLANS, SPECTATOR_PLANS as SPECTATOR } from "@/lib/plans";

const RED = "#C8000F";
const GOLD = "#E6B35A";

const SoonBadge = () => (
  <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
    style={{ background: "rgba(230,179,90,0.15)", color: GOLD }}>
    <Clock className="w-2.5 h-2.5" /> Soon
  </span>
);

export default function ChoosePlan() {
  const { user, ready } = useAuth();
  const { refresh, wallet } = useCredits();
  const nav = useNavigate();
  const [saving, setSaving] = useState(null);

  if (ready && !user) return <Navigate to="/login" replace />;

  const isFanView = user?.account_type === "fan";
  const trialUsed = !!wallet?.trial_used;

  const startTrial = async (id) => {
    setSaving("trial-" + id);
    try {
      await api.post("/credits/start-trial", { plan: id });
      await refresh();
      toast.success(`Your 7-day ${SPECTATOR.find((s) => s.id === id)?.name} trial has started — enjoy!`);
      nav("/riders");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.message) || "Couldn't start your trial — please try again.");
      setSaving(null);
    }
  };

  const choose = async (id) => {
    setSaving(id);
    try {
      if (id === "rookie") {
        await api.post("/credits/plan", { plan: id });
        await refresh();
        nav("/");
        return;
      }
      const { data } = await api.post("/payments/checkout", { lookup_key: id, origin_url: window.location.origin });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      throw new Error("no url");
    } catch (e) {
      toast.error("Couldn't start checkout — please try again.");
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F16] text-slate-100 px-5 py-12 sm:py-16" data-testid="choose-plan-page">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => nav("/")} data-testid="choose-plan-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-slate-200 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to app
        </button>
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full" style={{ background: "rgba(230,179,90,0.15)", color: GOLD }}>
            <Eye className="w-3.5 h-3.5" /> {isFanView ? "Follow more of the riders you love" : "Welcome — you've got free credits to start"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-5">{isFanView ? "Pick your spectator plan" : "Choose how you rodeo"}</h1>
          <p className="mt-3 text-slate-400">{isFanView ? "Follow more cowboys & cowgirls and get their entries, draws, results and videos — change anytime." : "Pick a plan to get going — you can change it anytime. Start free with Rookie if you're not sure."}</p>
        </div>

        {!isFanView && (
        <div className="grid lg:grid-cols-3 gap-5 mt-10 items-stretch">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id} data-testid={`choose-plan-${p.id}`}
                className={`relative flex flex-col rounded-2xl p-6 ${p.popular ? "border-2 bg-[#141A24]" : "border border-white/10 bg-[#121823]"}`}
                style={p.popular ? { borderColor: RED, boxShadow: "0 0 40px rgba(200,0,15,0.22)" } : {}}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full" style={{ background: RED }}>Most Popular</span>}
                <div className="flex items-center gap-2" style={{ color: p.accent }}><Icon className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-semibold">{p.name}</span></div>
                <div className="mt-2 flex items-end gap-1">
                  <span className="font-display text-4xl font-extrabold">{p.price}</span>
                  {p.unit && <span className="text-slate-400 text-sm mb-1.5">{p.unit}</span>}
                </div>
                <p className="text-sm text-slate-400 mt-2 min-h-[40px]">{p.desc}</p>
                <ul className="mt-4 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f.label} className={`flex gap-2.5 text-sm ${f.head ? "text-slate-400 text-xs uppercase tracking-wide pt-1" : "text-slate-200"}`}>
                      {!f.head && <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: f.soon ? "#6B7280" : p.accent }} />}
                      <span className={f.soon ? "text-slate-400" : ""}>{f.label}</span>
                      {f.soon && <SoonBadge />}
                    </li>
                  ))}
                </ul>
                <button onClick={() => choose(p.id)} disabled={saving} data-testid={`choose-plan-btn-${p.id}`}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-white disabled:opacity-60 transition-transform hover:scale-[1.02]"
                  style={{ background: p.popular ? RED : "rgba(255,255,255,0.08)" }}>
                  {saving === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {p.id === "rookie" ? "Start Free" : `Choose ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
        )}

        {!isFanView && (
        <div className="text-center mt-8">
          <button onClick={() => nav("/")} data-testid="choose-plan-skip" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white">
            Skip for now — continue as Rookie <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        )}

        {/* Spectator plans — for fans who just want to follow competitors */}
        <div className={isFanView ? "mt-10" : "mt-14 pt-10 border-t border-white/10"}>
          {!isFanView && (
          <div className="text-center max-w-xl mx-auto">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>
              <Eye className="w-3.5 h-3.5" /> Just watching?
            </div>
            <h2 className="font-display text-2xl font-extrabold mt-3">Spectator plans</h2>
            <p className="mt-2 text-slate-400 text-sm">Not competing? Follow the cowboys & cowgirls you care about — entries, draws, results and videos.</p>
            {!trialUsed && <p className="mt-2 text-[13px] font-semibold" style={{ color: GOLD }}>Try any plan free for 7 days — no card needed.</p>}
          </div>
          )}
          {isFanView && !trialUsed && <p className="text-center text-[13px] font-semibold mb-2" style={{ color: GOLD }}>Try any plan free for 7 days — no card needed.</p>}
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {SPECTATOR.map((s) => (
              <div key={s.id} data-testid={`choose-plan-${s.id}`}
                className={`relative flex flex-col rounded-2xl p-6 ${s.popular ? "border-2 bg-[#141A24]" : "border border-white/10 bg-[#121823]"}`}
                style={s.popular ? { borderColor: GOLD, boxShadow: "0 0 30px rgba(230,179,90,0.18)" } : {}}>
                {s.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold uppercase tracking-wide text-[#1A1205] px-3 py-1 rounded-full" style={{ background: GOLD }}>Most Popular</span>}
                <div className="flex items-center gap-2" style={{ color: GOLD }}><Eye className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-semibold">Spectator</span></div>
                <h3 className="font-display text-xl font-bold mt-1">{s.name}</h3>
                <div className="mt-2 flex items-end gap-1"><span className="font-display text-3xl font-extrabold">{s.price}</span><span className="text-slate-400 text-sm mb-1">/mo</span></div>
                <p className="text-sm text-slate-300 mt-2">{s.desc}</p>
                <p className="text-xs text-slate-500 mt-1 flex-1">{s.extra}</p>
                <button onClick={() => choose(s.id)} disabled={!!saving} data-testid={`choose-plan-btn-${s.id}`}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-xl text-[#1A1205] disabled:opacity-60 transition-transform hover:scale-[1.02]" style={{ background: GOLD }}>
                  {saving === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Choose {s.name}
                </button>
                {!trialUsed && (
                  <button onClick={() => startTrial(s.id)} disabled={!!saving} data-testid={`choose-plan-trial-${s.id}`}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 rounded-xl border border-white/15 text-slate-200 hover:bg-white/5 disabled:opacity-60 transition-colors">
                    {saving === `trial-${s.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Start 7-day free trial
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
